import { useMemo, useSyncExternalStore } from "react";

/**
 * Creates an observable
 * @internal
 */
function store<T>(initialValue: T): Store<T> {
  const listeners = new Set<(value: T) => void>();
  let currentValue = initialValue;

  return {
    get: () => currentValue,

    set: (newValue: T) => {
      if (Object.is(currentValue, newValue)) {
        return;
      }

      currentValue = newValue;
      for (const listener of listeners) {
        listener(newValue);
      }
    },

    subscribe: (listener: (value: T) => void) => {
      listener(currentValue);
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

/**
 * Adds all the methods to a store so they dispatch based on the current state
 * @internal
 */
function attachMethods<M extends Methods>(store: Store<keyof M>, methods: M): Controller<M> {
  // oxlint-disable-next-line typescript/no-explicit-any -- avoids return type mismatch
  const dispatchMethods: any = {};

  for (const [_stateKey, handlers] of Object.entries(methods)) {
    for (const methodKey of Object.keys(handlers)) {
      if (methodKey in dispatchMethods || methodKey in store) {
        continue;
      }

      dispatchMethods[methodKey] = (...args: unknown[]) => {
        const currentState = store.get();
        const availableMethods = methods[currentState];
        return availableMethods?.[methodKey]?.(...args);
      };
    }
  }

  return merge(store, dispatchMethods);
}

/**
 * Shallow merges objects using Object.assign
 * @internal
 */
function merge<Target extends object, Objects extends object[]>(
  target: Target,
  ...objects: Objects
): MergeArrayOfObjects<Target, Objects> {
  return Object.assign(target, ...objects);
}

/**
 * Creates a controller - an observable that runs different methods based on the current state.
 *
 * When you call a method, it runs the version defined for the current state. If that state
 * doesn't have that method, it's a no-op.
 *
 * @param defaultState - Starting state
 * @param methods - Object mapping states to their available methods
 * @returns Observable with all possible methods attached
 *
 * @example
 * const ctrl = controller("idle", {
 *   idle: { start: () => ctrl.set("running") },
 *   running: { stop: () => ctrl.set("idle") }
 * });
 *
 * ctrl.get();    // "idle"
 * ctrl.start();  // works - changes to "running"
 * ctrl.start();  // no-op - "running" doesn't have start
 * ctrl.stop();   // works - changes back to "idle"
 */
function controller<M extends Methods>(defaultState: keyof M, methods: M): Prettify<Controller<M>> {
  const s = store(defaultState);
  return attachMethods(s, methods);
}

type ReservedKeys = "get" | "set" | "subscribe";

declare const __methods: unique symbol;

export type Methods = Record<string, Record<string, AnyFn>>;

/** Store interface for observable state */
type Store<T> = {
  get: () => T;
  set: (value: T) => void;
  subscribe: (listener: (value: T) => void) => () => void;
};

/** Inferred methods from state methods object - dispatchers created from union intersection */
type DispatchMethods<M extends Methods> = UnionToIntersection<M[keyof M]>;

/** Controller with store methods and dynamic method dispatchers */
export type Controller<M extends Methods> = Store<keyof M> &
  Omit<DispatchMethods<M>, ReservedKeys> & {
    readonly [__methods]?: M;
  };

// oxlint-disable-next-line typescript/no-explicit-any -- flexible function signature
type AnyFn = (...args: any[]) => void;

/** Converts union of function types to intersection */
// oxlint-disable-next-line typescript/no-explicit-any -- distributive conditional type
type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (k: infer I extends U) => void
  ? I
  : never;

/** Recursively merges array of objects */
type MergeArrayOfObjects<T1 extends object, Objects extends object[]> = Objects extends [
  infer T2 extends object,
  ...infer Rest extends object[],
]
  ? MergeArrayOfObjects<Merge<T1, T2>, Rest>
  : T1;

/** Merges two object types with right taking precedence */
type Merge<T1, T2> = Omit<T1, keyof T2> & T2;

/** Pretty-prints a type for better IntelliSense display */
type Prettify<T> = {
  [K in keyof T]: T[K];
} & {};

/**
 * Hook that creates a controller and subscribes to its state changes.
 *
 * @param defaultState - The initial state
 * @param methods - State-dependent methods
 * @returns The controller instance with reactive state
 *
 * @example
 * ```tsx
 * function Button() {
 *   const handleClick = async () => {
 *     button.set("loading");
 *     await submitForm();
 *     button.set("idle");
 *   };
 *
 *   const button = useController("idle", {
 *     idle: { onClick: handleClick },
 *     loading: {},
 *   });
 *
 *   const state = button.get();
 *
 *   return (
 *     <button onClick={button.onClick} disabled={state === "loading"}>
 *       {state === "loading" ? "Loading..." : "Submit"}
 *     </button>
 *   );
 * }
 * ```
 */
export function useController<M extends Methods>(defaultState: keyof M, methods: M): Controller<M> {
  // oxlint-disable-next-line react-hooks/exhaustive-deps, typescript/no-explicit-any -- controller has to have stable identity; useSynExternalStore modifies the type a bit, but we need to return Controller<M>
  const ctrl: any = useMemo(() => controller(defaultState, methods), []); // oxlint-disable-line react-hooks/exhaustive-deps -- existing dependency list; clearing these is tracked separately (AISYS-12016)

  useSyncExternalStore(ctrl.subscribe, ctrl.get, ctrl.get);

  return ctrl;
}
